import { Button, Pause, Play, RotateCcw, cn, useTheme } from "@app/ui";
import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../../lib/motion/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/settings.store.js";
import { useThemeStore } from "../../stores/theme.store.js";
import { DEMO_HOST_MSG_SOURCE, isDemoStatusMsg, type DemoHostMsg } from "./demo-bridge.js";
import { buildDemoUrl } from "./demo-params.js";
import { DEMO_SCRIPTS, type DemoScriptId } from "./registry.js";

type PlayerStatus = "loading" | "running" | "done" | "error";

/** If the iframe never reports ready (blocked iframe, broken boot), degrade to
 *  the failure panel instead of showing a dead frame. */
const READY_TIMEOUT_MS = 10_000;

interface Props {
  id: DemoScriptId;
  className?: string;
  loop?: boolean;
  autoplay?: boolean;
  /** Hide the title/replay footer — for step-less "live preview" scripts where
   *  replaying has nothing to show. */
  footer?: boolean;
}

/**
 * Host-side embed of a ghost demo: a lazy same-origin iframe rendered at the
 * script's logical stage size and CSS-scaled to the container. Watch-only
 * (`pointer-events: none`); host theme changes are forwarded live so a running
 * demo re-themes without restarting.
 */
export function DemoPlayer({ id, className, loop = true, autoplay = true, footer = true }: Props) {
  const script = DEMO_SCRIPTS[id];
  const { t } = useI18n();
  const preset = useThemeStore((s) => s.config.preset);
  const { resolved } = useTheme();
  const lang = useSettingsStore((s) => s.language);
  const reduce = useReducedMotion();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [status, setStatus] = useState<PlayerStatus>("loading");
  // Bumped to remount the iframe after a failure (the error panel unmounted it).
  const [attempt, setAttempt] = useState(0);
  const [paused, setPaused] = useState(false);
  // Pause control visibility: shown on hover while the mouse moves, fades out
  // when it leaves or goes still. Stays up while paused (it's the resume affordance).
  const [controlsVisible, setControlsVisible] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);

  function pokeControls() {
    setControlsVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControlsVisible(false), 1800);
  }
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  // Lazy: create the iframe only once the player scrolls into view. The URL is
  // built at that moment (captures current theme/lang); later changes flow via
  // postMessage instead of a restart.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || src) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSrc(buildDemoUrl(id, { preset, mode: resolved, lang, loop, autoplay, reduce: !!reduce }));
          io.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src, id, preset, resolved, lang, loop, autoplay, reduce]);

  // Scale the fixed logical stage to the container width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setScale(el.clientWidth / script.stage.width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [script.stage.width]);

  // Status from the iframe (validated by shape + sender window).
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow || !isDemoStatusMsg(e.data)) return;
      if (e.data.type === "ready" || e.data.type === "step") setStatus("running");
      else if (e.data.type === "done") setStatus("done");
      else setStatus("error");
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!src) return;
    const timer = window.setTimeout(() => setStatus((s) => (s === "loading" ? "error" : s)), READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [src, attempt]);

  // Forward live theme changes into the running demo.
  useEffect(() => {
    const w = iframeRef.current?.contentWindow;
    if (!w || status === "loading") return;
    const msg: DemoHostMsg = { source: DEMO_HOST_MSG_SOURCE, type: "setTheme", preset, mode: resolved };
    w.postMessage(msg, "*");
  }, [preset, resolved, status]);

  function replay() {
    if (status === "error") {
      // The failed iframe was unmounted — remount it fresh with a new ready timeout.
      setAttempt((a) => a + 1);
      setStatus("loading");
      setPaused(false);
      return;
    }
    // Replaying implies playing — release a pause left behind.
    if (paused) setPausedBoth(false);
    const msg: DemoHostMsg = { source: DEMO_HOST_MSG_SOURCE, type: "replay" };
    iframeRef.current?.contentWindow?.postMessage(msg, "*");
    setStatus("running");
  }

  function setPausedBoth(next: boolean) {
    setPaused(next);
    const msg: DemoHostMsg = { source: DEMO_HOST_MSG_SOURCE, type: "setPaused", paused: next };
    iframeRef.current?.contentWindow?.postMessage(msg, "*");
  }

  return (
    <div className={cn("overflow-hidden rounded-lg border bg-card", className)}>
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden bg-background"
        style={{ aspectRatio: `${script.stage.width} / ${script.stage.height}` }}
        onMouseMove={pokeControls}
        onMouseLeave={() => {
          window.clearTimeout(hideTimer.current);
          setControlsVisible(false);
        }}
      >
        {src && status !== "error" && (
          <iframe
            key={attempt}
            ref={iframeRef}
            src={src}
            title={t(script.titleKey)}
            tabIndex={-1}
            className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
            style={{ width: script.stage.width, height: script.stage.height, transform: `scale(${scale})` }}
          />
        )}
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {t("demos.loading")}
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-muted-foreground">
            {t("demos.failed")}
          </div>
        )}
        {src && status !== "error" && status !== "loading" && script.steps.length > 0 && (
          <button
            type="button"
            onClick={() => setPausedBoth(!paused)}
            aria-label={t(paused ? "demos.resume" : "demos.pause")}
            title={t(paused ? "demos.resume" : "demos.pause")}
            className={cn(
              "absolute bottom-2 right-2 z-10 flex h-9 w-9 items-center justify-center rounded-full border bg-background/85 text-foreground shadow-md backdrop-blur-sm transition-opacity duration-300 hover:bg-background",
              controlsVisible || paused ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        )}
      </div>
      {footer && (
        <div className="flex items-center justify-between gap-2 border-t px-3 py-1.5">
          <span className="truncate text-xs text-muted-foreground">{t(script.titleKey)}</span>
          <Button variant="ghost" size="sm" onClick={replay} disabled={!src || status === "loading"}>
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            {t("demos.replay")}
          </Button>
        </div>
      )}
    </div>
  );
}
